import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "hidden", "results"]
  static values = {
    url: String,
    type: String,
    searchingText: String,
    emptyText: String,
    loadingMoreText: String,
    params: Object,
    dependencyName: String,
    dependencyParam: String,
    createUrl: String,
    createText: String,
    clearOnSelect: Boolean,
  }

  connect() {
    this.index = -1
    this.selectedText = this.inputTarget.value
    this.abortController = null
    this.nextPage = null
    this.loadingMore = false
    this.sentinelObserver = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) this.loadMore()
    }, { root: this.resultsTarget, rootMargin: "0px 0px 80px" })
    this.outsideClick = (event) => { if (!this.element.contains(event.target)) this.close() }
    document.addEventListener("click", this.outsideClick)
    this.form?.addEventListener("change", this.dependencyChanged)
  }

  disconnect() {
    document.removeEventListener("click", this.outsideClick)
    this.form?.removeEventListener("change", this.dependencyChanged)
    this.abortController?.abort()
    this.sentinelObserver.disconnect()
    clearTimeout(this.timeout)
  }

  search(event) {
    if (event?.type === "input" && this.inputTarget.value !== this.selectedText) this.setHiddenValue("")
    this.queueSearch(this.inputTarget.value.trim())
  }

  focus() {
    const hasSelection = this.hiddenTarget.value && this.inputTarget.value === this.selectedText
    if (hasSelection) this.inputTarget.select()
    this.queueSearch(hasSelection ? "" : this.inputTarget.value.trim())
  }

  queueSearch(query) {
    clearTimeout(this.timeout)
    this.abortController?.abort()
    this.resetPagination()
    this.query = query
    this.showLoading()
    this.timeout = setTimeout(() => this.load(1), 200)
  }

  async load(page = 1, { append = false } = {}) {
    this.abortController = new AbortController()
    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("type", this.typeValue)
    url.searchParams.set("q", this.query)
    url.searchParams.set("page", page)
    if (this.hasParamsValue) {
      Object.entries(this.paramsValue).forEach(([key, value]) => url.searchParams.set(key, value))
    }
    if (this.hasDependencyNameValue) {
      const value = this.form?.elements.namedItem(this.dependencyNameValue)?.value
      if (!value) return this.render([], { append: false, nextPage: null })
      url.searchParams.set(this.dependencyParamValue, value)
    }
    try {
      const response = await fetch(url, { headers: { Accept: "application/json" }, signal: this.abortController.signal })
      if (!response.ok) return append ? this.resetPagination() : this.render([])
      const payload = await response.json()
      this.render(payload.results, { append, nextPage: payload.next_page })
    } catch (error) {
      if (error.name !== "AbortError") append ? this.resetPagination() : this.render([])
    }
  }

  render(records, { append = false, nextPage = null } = {}) {
    if (!append && records.length === 0) {
      this.renderStatus(this.emptyTextValue, "search-x")
      this.appendCreateOption()
      return
    }

    const options = records.map((record) => {
      const button = document.createElement("button")
      button.type = "button"
      button.className = "block w-full rounded-md px-3 py-2 text-left text-sm hover:bg-orange-50"
      button.textContent = record.text
      button.dataset.id = record.id
      button.setAttribute("role", "option")
      button.addEventListener("click", () => this.select(record))
      return button
    })
    this.removeSentinel()
    this.removeCreateOption()
    append ? this.resultsTarget.append(...options) : this.resultsTarget.replaceChildren(...options)
    this.nextPage = nextPage
    this.loadingMore = false
    if (nextPage) this.appendSentinel()
    this.appendCreateOption()
    if (!append) this.index = 0
    this.highlight()
    this.open()
  }

  loadMore() {
    if (!this.nextPage || this.loadingMore) return

    this.loadingMore = true
    this.sentinelObserver.disconnect()
    this.load(this.nextPage, { append: true })
  }

  appendSentinel() {
    const sentinel = document.createElement("div")
    sentinel.dataset.autocompleteSentinel = ""
    sentinel.className = "flex items-center justify-center gap-2 border-t border-slate-100 px-3 py-3 text-sm text-slate-500"

    const spinner = document.createElement("span")
    spinner.className = "size-4 animate-spin rounded-full border-2 border-slate-200 border-t-slate-500"
    const label = document.createElement("span")
    label.textContent = this.loadingMoreTextValue
    sentinel.append(spinner, label)
    this.resultsTarget.append(sentinel)
    this.sentinelObserver.observe(sentinel)
  }

  removeSentinel() {
    this.sentinelObserver.disconnect()
    this.resultsTarget.querySelector("[data-autocomplete-sentinel]")?.remove()
  }

  appendCreateOption() {
    if (!this.hasCreateUrlValue || !this.hasCreateTextValue || !this.query) return

    const url = new URL(this.createUrlValue, window.location.origin)
    url.searchParams.set("name", this.query)
    const link = document.createElement("a")
    link.href = url.toString()
    link.dataset.turboFrame = "modal"
    link.dataset.autocompleteCreateOption = ""
    link.className = "block w-full rounded-md border-t border-slate-100 px-3 py-2 text-left text-sm font-semibold text-orange-700 hover:bg-orange-50"
    link.textContent = this.createTextValue.replace("%{query}", this.query)
    link.setAttribute("role", "option")
    link.addEventListener("click", () => this.close())
    this.resultsTarget.append(link)
  }

  removeCreateOption() {
    this.resultsTarget.querySelector("[data-autocomplete-create-option]")?.remove()
  }

  resetPagination() {
    this.nextPage = null
    this.loadingMore = false
    this.removeSentinel()
  }

  select(record) {
    this.inputTarget.value = record.text
    this.selectedText = record.text
    this.setHiddenValue(record.id)
    this.element.dispatchEvent(new CustomEvent("autocomplete:selected", { detail: { record }, bubbles: true }))
    this.close()
    if (this.clearOnSelectValue) this.clear()
  }

  clear() {
    this.inputTarget.value = ""
    this.selectedText = ""
    this.setHiddenValue("")
  }

  dependencyChanged = (event) => {
    if (!this.hasDependencyNameValue || event.target.name !== this.dependencyNameValue) return

    this.inputTarget.value = ""
    this.selectedText = ""
    this.setHiddenValue("")
    this.close()
  }

  setHiddenValue(value) {
    if (this.hiddenTarget.value.toString() === value.toString()) return

    this.hiddenTarget.value = value
    this.hiddenTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  get form() {
    return this.element.closest("form")
  }

  keydown(event) {
    const options = [...this.resultsTarget.querySelectorAll("[role='option']")]
    if (event.key === "Escape") return this.close()
    if (!options.length) return
    if (event.key === "ArrowDown") this.index = Math.min(this.index + 1, options.length - 1)
    else if (event.key === "ArrowUp") this.index = Math.max(this.index - 1, 0)
    else if (event.key === "Enter") return options[this.index]?.click()
    else return
    event.preventDefault()
    this.highlight()
  }

  showLoading() {
    this.renderStatus(this.searchingTextValue, null, true)
  }

  renderStatus(text, iconName, loading = false) {
    this.resetPagination()
    const status = document.createElement("div")
    status.className = "flex items-center justify-center gap-2 px-3 py-4 text-sm text-slate-500"

    if (loading) {
      const spinner = document.createElement("span")
      spinner.className = "size-4 animate-spin rounded-full border-2 border-slate-200 border-t-slate-500"
      status.append(spinner)
    } else {
      const icon = document.createElement("i")
      icon.dataset.lucide = iconName
      icon.className = "size-4 text-slate-400"
      status.append(icon)
    }

    const label = document.createElement("span")
    label.textContent = text
    status.append(label)
    this.resultsTarget.replaceChildren(status)
    this.index = -1
    this.open()
  }

  highlight() {
    const options = [...this.resultsTarget.querySelectorAll("[role='option']")]
    options.forEach((option, index) => {
      const selected = index === this.index
      option.classList.toggle("bg-orange-50", selected)
      option.setAttribute("aria-selected", selected.toString())
    })
  }

  open() {
    this.resultsTarget.classList.remove("hidden")
    this.inputTarget.setAttribute("aria-expanded", "true")
  }

  close() {
    clearTimeout(this.timeout)
    this.abortController?.abort()
    this.resultsTarget.classList.add("hidden")
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.index = -1
  }
}
